/**
 * Marketplace Hooks
 *
 * Search, filtering, matching, and discovery functionality.
 */

import { useMemo, useState, useCallback } from 'react';
import { useProfileStore } from './profile-store';
import { InvestorProfile, DealflowStartup } from './inverio-store';
import { useDealflowStore } from './dealflow-store';
import type { StartupSummary } from '@/lib/supabase/edge-functions';
import { appearsOn, dataRoomOpen, type DealStage } from '@/lib/dealflow/stage';

// ============================================
// Types
// ============================================

export interface SearchFilters {
  query: string;
  industries: string[];
  stages: string[];
  locations: string[];
  scoreRange: { min: number; max: number };
  fundingRange: { min: number; max: number };
  hasDataRoom: boolean | null;
  status: string[];
}

export interface MatchScore {
  overall: number;
  industryMatch: number;
  stageMatch: number;
  locationMatch: number;
  ticketMatch: number;
  reasons: string[];
}

export interface SavedItem {
  id: string;
  type: 'startup' | 'investor';
  itemId: string;
  savedAt: string;
  listId?: string;
  notes?: string;
}

export interface SavedList {
  id: string;
  name: string;
  type: 'startup' | 'investor';
  createdAt: string;
  itemCount: number;
}

// ============================================
// Constants
// ============================================

export const INDUSTRIES = [
  'FinTech',
  'HealthTech',
  'EdTech',
  'CleanTech',
  'AI/ML',
  'SaaS',
  'E-Commerce',
  'Marketplace',
  'DeepTech',
  'Consumer',
  'Enterprise',
  'Cybersecurity',
];

export const STAGES = ['pre-seed', 'seed', 'series-a', 'series-b', 'series-c+'];

export const STAGE_LABELS: Record<string, string> = {
  'pre-seed': 'Pre-Seed',
  'seed': 'Seed',
  'series-a': 'Series A',
  'series-b': 'Series B',
  'series-c+': 'Series C+',
};

export const LOCATIONS = [
  'Zurich, Switzerland',
  'Geneva, Switzerland',
  'Basel, Switzerland',
  'San Francisco, USA',
  'New York, USA',
  'London, UK',
  'Berlin, Germany',
  'Paris, France',
  'Singapore',
  'Remote',
];

export const DEFAULT_FILTERS: SearchFilters = {
  query: '',
  industries: [],
  stages: [],
  locations: [],
  scoreRange: { min: 0, max: 100 },
  fundingRange: { min: 0, max: 50000000 },
  hasDataRoom: null,
  status: [],
};

/**
 * Whether a dealflow item passes the status filter.
 * - Explicit selection → exact match (how the "Rejected" archive is viewed:
 *   status: ['rejected']).
 * - No selection → the active pipeline: membership of the `dealflow` surface
 *   (see `src/lib/dealflow/stage.ts`). This used to be a hardcoded
 *   `dealStatus !== 'rejected'`, which only ever excluded one stage — so
 *   `invested` (→ portfolio) and `monitoring` (→ watchlist) stayed stuck in
 *   the pipeline forever, because placement was reading a filter chip instead
 *   of the stage.
 *
 * Pure + exported so the surface-membership behavior is unit-testable.
 */
export function matchesDealflowStatusFilter(
  dealStatus: DealStage,
  statusFilter: string[],
): boolean {
  if (statusFilter.length > 0) return statusFilter.includes(dealStatus);
  return appearsOn(dealStatus, 'dealflow');
}

// ============================================
// Smart Matching Algorithm
// ============================================

/**
 * Calculate match score between an investor and a startup
 */
export function calculateMatchScore(
  startup: StartupSummary,
  investor: InvestorProfile,
  readinessScore: number = 70
): MatchScore {
  const reasons: string[] = [];
  let industryMatch = 0;
  let stageMatch = 0;
  let locationMatch = 0;
  let ticketMatch = 0;

  const industry = startup.company.industry;
  const stage = startup.company.stage;
  const location = startup.company.location ?? '';
  const fundingTarget = startup.funding.currentRaise?.amount ?? 0;

  // Industry Match (0-100)
  if (investor.investmentFocus.some(focus =>
    industry.toLowerCase().includes(focus.toLowerCase()) ||
    focus.toLowerCase().includes(industry.toLowerCase())
  )) {
    industryMatch = 100;
    reasons.push(`Matches ${investor.investmentFocus.find(f =>
      industry.toLowerCase().includes(f.toLowerCase())
    ) || industry} focus`);
  } else {
    // Partial match for related industries
    industryMatch = 30;
  }

  // Stage Match (0-100)
  if (investor.stagePreference.includes(stage)) {
    stageMatch = 100;
    reasons.push(`${STAGE_LABELS[stage]} stage preferred`);
  } else if (investor.stagePreference.length === 0) {
    stageMatch = 50; // No preference = neutral
  } else {
    stageMatch = 20;
  }

  // Location Match (0-100)
  const startupCity = location.split(',')[0].trim().toLowerCase();
  const investorCity = investor.location.split(',')[0].trim().toLowerCase();
  if (startupCity === investorCity) {
    locationMatch = 100;
    reasons.push(`Same location: ${location.split(',')[0]}`);
  } else if (location.includes('Switzerland') && investor.location.includes('Switzerland')) {
    locationMatch = 80;
    reasons.push('Both in Switzerland');
  } else if (location.includes('Remote')) {
    locationMatch = 60;
    reasons.push('Open to remote');
  } else {
    locationMatch = 30;
  }

  // Ticket Size Match (0-100)
  const targetInRange =
    fundingTarget >= investor.ticketSize.min &&
    fundingTarget <= investor.ticketSize.max;

  if (targetInRange) {
    ticketMatch = 100;
    reasons.push(`Funding target within ticket range`);
  } else if (fundingTarget < investor.ticketSize.min) {
    // Slightly below range
    const ratio = fundingTarget / investor.ticketSize.min;
    ticketMatch = Math.max(20, ratio * 100);
  } else {
    // Above range
    const ratio = investor.ticketSize.max / fundingTarget;
    ticketMatch = Math.max(20, ratio * 100);
  }

  // Add readiness score bonus
  if (readinessScore >= 80) {
    reasons.push('High readiness score (80+)');
  } else if (readinessScore >= 60) {
    reasons.push('Good readiness score (60+)');
  }

  // Calculate overall weighted score
  const overall = Math.round(
    industryMatch * 0.35 +
    stageMatch * 0.25 +
    ticketMatch * 0.25 +
    locationMatch * 0.15
  );

  return {
    overall,
    industryMatch: Math.round(industryMatch),
    stageMatch: Math.round(stageMatch),
    locationMatch: Math.round(locationMatch),
    ticketMatch: Math.round(ticketMatch),
    reasons: reasons.slice(0, 3), // Top 3 reasons
  };
}

// ============================================
// Search & Filter Hook
// ============================================

export function useSearch<T>(
  items: T[],
  searchFn: (item: T, query: string) => boolean,
  filterFn: (item: T, filters: SearchFilters) => boolean
) {
  const [filters, setFilters] = useState<SearchFilters>(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState<string>('relevance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const filteredItems = useMemo(() => {
    let result = items;

    // Apply search query
    if (filters.query.trim()) {
      result = result.filter(item => searchFn(item, filters.query.toLowerCase()));
    }

    // Apply filters
    result = result.filter(item => filterFn(item, filters));

    return result;
  }, [items, filters, searchFn, filterFn]);

  const updateFilter = useCallback(<K extends keyof SearchFilters>(
    key: K,
    value: SearchFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const toggleArrayFilter = useCallback(<K extends keyof SearchFilters>(
    key: K,
    value: string
  ) => {
    setFilters(prev => {
      const arr = prev[key] as string[];
      const newArr = arr.includes(value)
        ? arr.filter(v => v !== value)
        : [...arr, value];
      return { ...prev, [key]: newArr };
    });
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.query) count++;
    if (filters.industries.length) count++;
    if (filters.stages.length) count++;
    if (filters.locations.length) count++;
    if (filters.scoreRange.min > 0 || filters.scoreRange.max < 100) count++;
    if (filters.fundingRange.min > 0 || filters.fundingRange.max < 50000000) count++;
    if (filters.hasDataRoom !== null) count++;
    if (filters.status.length) count++;
    return count;
  }, [filters]);

  return {
    filters,
    setFilters,
    updateFilter,
    resetFilters,
    toggleArrayFilter,
    filteredItems,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    activeFilterCount,
  };
}

// ============================================
// Dealflow Search Hook
// ============================================

/**
 * Search/filter/sort over the investor's dealflow.
 *
 * @param extraItems Optional additional DealflowStartup items (e.g. the
 * raising-startups feed on the Discover screen). Items whose startup already
 * appears in the pipeline are deduped away.
 */
export function useDealflowSearch(extraItems?: DealflowStartup[]) {
  const dealflow = useDealflowStore(s => s.dealflow);
  const investorProfile = useProfileStore(s => s.investorProfile);

  const searchableItems = useMemo(() => {
    if (!extraItems || extraItems.length === 0) return dealflow;
    const pipelineIds = new Set(dealflow.flatMap(d => [d.id, d.startup.id]));
    return [
      ...dealflow,
      ...extraItems.filter(e => !pipelineIds.has(e.id) && !pipelineIds.has(e.startup.id)),
    ];
  }, [dealflow, extraItems]);

  const searchFn = useCallback((deal: DealflowStartup, query: string) => {
    const startup = deal.startup;
    return (
      startup.company.name.toLowerCase().includes(query) ||
      startup.company.tagline.toLowerCase().includes(query) ||
      startup.company.industry.toLowerCase().includes(query) ||
      (startup.company.location ?? '').toLowerCase().includes(query)
    );
  }, []);

  const filterFn = useCallback((deal: DealflowStartup, filters: SearchFilters) => {
    const startup = deal.startup;

    // Industry filter
    if (filters.industries.length > 0 && !filters.industries.includes(startup.company.industry)) {
      return false;
    }

    // Stage filter
    if (filters.stages.length > 0 && !filters.stages.includes(startup.company.stage)) {
      return false;
    }

    // Location filter
    if (filters.locations.length > 0) {
      const matchesLocation = filters.locations.some(loc =>
        (startup.company.location ?? '').toLowerCase().includes(loc.toLowerCase())
      );
      if (!matchesLocation) return false;
    }

    // Score range
    // An unscored deal cannot satisfy a score range. Excluded when the filter
    // is narrowed from the full 0-100 default, kept when it is not — so
    // "no score" never masquerades as a low score.
    if (deal.readinessScore == null) {
      const rangeIsNarrowed =
        filters.scoreRange.min > 0 || filters.scoreRange.max < 100;
      if (rangeIsNarrowed) return false;
    } else if (
      deal.readinessScore < filters.scoreRange.min ||
      deal.readinessScore > filters.scoreRange.max
    ) {
      return false;
    }

    // Funding range
    const fundingTarget = startup.funding.currentRaise?.amount ?? 0;
    if (fundingTarget < filters.fundingRange.min ||
        fundingTarget > filters.fundingRange.max) {
      return false;
    }

    // Data room filter. "Has a data room" means the founder has opened it to
    // THIS investor, which the stage carries (`diligence`) since the second
    // store, dealflow.data_room_access, was retired in 20260805160000.
    if (filters.hasDataRoom !== null && dataRoomOpen(deal.status) !== filters.hasDataRoom) {
      return false;
    }

    // Status filter (explicit → exact; none → hide passed archive). See
    // matchesDealflowStatusFilter.
    if (!matchesDealflowStatusFilter(deal.status, filters.status)) {
      return false;
    }

    return true;
  }, []);

  const search = useSearch(searchableItems, searchFn, filterFn);

  // Add match scores
  const itemsWithMatch = useMemo(() => {
    if (!investorProfile) return search.filteredItems.map(deal => ({ deal, matchScore: null }));

    return search.filteredItems.map(deal => ({
      deal,
      matchScore: calculateMatchScore(deal.startup, investorProfile, deal.readinessScore ?? undefined),
    }));
  }, [search.filteredItems, investorProfile]);

  // Sort by match score or other criteria
  const sortedItems = useMemo(() => {
    const items = [...itemsWithMatch];

    switch (search.sortBy) {
      case 'match':
        items.sort((a, b) => (b.matchScore?.overall || 0) - (a.matchScore?.overall || 0));
        break;
      case 'score':
        // Unscored sorts last: absence is not a low score, but must not outrank one.
        items.sort((a, b) => (b.deal.readinessScore ?? -1) - (a.deal.readinessScore ?? -1));
        break;
      case 'funding':
        items.sort((a, b) => (b.deal.startup.funding.currentRaise?.amount ?? 0) - (a.deal.startup.funding.currentRaise?.amount ?? 0));
        break;
      case 'recent':
        // By lastActivity - would need proper date parsing
        break;
      case 'relevance':
      default:
        // Default: New deals first, then by match score
        items.sort((a, b) => {
          if (a.deal.status === 'screening' && b.deal.status !== 'screening') return -1;
          if (b.deal.status === 'screening' && a.deal.status !== 'screening') return 1;
          return (b.matchScore?.overall || 0) - (a.matchScore?.overall || 0);
        });
    }

    if (search.sortOrder === 'asc') {
      items.reverse();
    }

    return items;
  }, [itemsWithMatch, search.sortBy, search.sortOrder]);

  return {
    ...search,
    itemsWithMatch: sortedItems,
  };
}

// ============================================
// Saved Items Hook
// ============================================

export function useSavedItems() {
  const [savedItems, setSavedItems] = useState<SavedItem[]>([]);
  const [savedLists, setSavedLists] = useState<SavedList[]>([
    { id: 'default', name: 'Saved', type: 'startup', createdAt: new Date().toISOString(), itemCount: 0 },
    { id: 'watchlist', name: 'Watchlist', type: 'startup', createdAt: new Date().toISOString(), itemCount: 0 },
  ]);

  const saveItem = useCallback((type: 'startup' | 'investor', itemId: string, listId?: string) => {
    const newItem: SavedItem = {
      id: `saved-${Date.now()}`,
      type,
      itemId,
      savedAt: new Date().toISOString(),
      listId: listId || 'default',
    };
    setSavedItems(prev => [...prev, newItem]);

    // Update list count
    setSavedLists(prev => prev.map(list =>
      list.id === (listId || 'default')
        ? { ...list, itemCount: list.itemCount + 1 }
        : list
    ));
  }, []);

  const unsaveItem = useCallback((itemId: string) => {
    const item = savedItems.find(s => s.itemId === itemId);
    if (item) {
      setSavedItems(prev => prev.filter(s => s.itemId !== itemId));
      setSavedLists(prev => prev.map(list =>
        list.id === item.listId
          ? { ...list, itemCount: Math.max(0, list.itemCount - 1) }
          : list
      ));
    }
  }, [savedItems]);

  const isItemSaved = useCallback((itemId: string) => {
    return savedItems.some(s => s.itemId === itemId);
  }, [savedItems]);

  const createList = useCallback((name: string, type: 'startup' | 'investor') => {
    const newList: SavedList = {
      id: `list-${Date.now()}`,
      name,
      type,
      createdAt: new Date().toISOString(),
      itemCount: 0,
    };
    setSavedLists(prev => [...prev, newList]);
    return newList.id;
  }, []);

  const deleteList = useCallback((listId: string) => {
    if (listId === 'default' || listId === 'watchlist') return; // Can't delete default lists
    setSavedItems(prev => prev.filter(s => s.listId !== listId));
    setSavedLists(prev => prev.filter(l => l.id !== listId));
  }, []);

  const getItemsInList = useCallback((listId: string) => {
    return savedItems.filter(s => s.listId === listId);
  }, [savedItems]);

  return {
    savedItems,
    savedLists,
    saveItem,
    unsaveItem,
    isItemSaved,
    createList,
    deleteList,
    getItemsInList,
  };
}

// ============================================
// Bulk Actions Hook
// ============================================

export function useBulkActions<T extends { id: string }>(items: T[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(items.map(i => i.id)));
  }, [items]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const enterSelectionMode = useCallback(() => {
    setIsSelectionMode(true);
  }, []);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const selectedItems = useMemo(() => {
    return items.filter(i => selectedIds.has(i.id));
  }, [items, selectedIds]);

  const isSelected = useCallback((id: string) => {
    return selectedIds.has(id);
  }, [selectedIds]);

  return {
    selectedIds,
    selectedItems,
    selectedCount: selectedIds.size,
    isSelectionMode,
    toggleSelection,
    selectAll,
    deselectAll,
    enterSelectionMode,
    exitSelectionMode,
    isSelected,
  };
}

// ============================================
// Comparison Hook
// ============================================

export function useComparison<T extends { id: string }>(maxItems: number = 4) {
  const [comparisonIds, setComparisonIds] = useState<string[]>([]);

  const addToComparison = useCallback((id: string) => {
    setComparisonIds(prev => {
      if (prev.includes(id)) return prev;
      if (prev.length >= maxItems) return prev;
      return [...prev, id];
    });
  }, [maxItems]);

  const removeFromComparison = useCallback((id: string) => {
    setComparisonIds(prev => prev.filter(i => i !== id));
  }, []);

  const clearComparison = useCallback(() => {
    setComparisonIds([]);
  }, []);

  const isInComparison = useCallback((id: string) => {
    return comparisonIds.includes(id);
  }, [comparisonIds]);

  const canAddMore = comparisonIds.length < maxItems;

  return {
    comparisonIds,
    comparisonCount: comparisonIds.length,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isInComparison,
    canAddMore,
    maxItems,
  };
}
